import * as THREE from 'three'

/**
 * Estimate the floor height of a loaded Spark SplatMesh by sampling splat
 * positions within an XZ radius of a target point and taking a low
 * percentile of their Y values.
 *
 * "Average Y" is tempting but wrong — it would include everything above
 * the floor (walls, ceiling, props), giving a value near the middle of
 * the room. We want the *floor*, so we sort and pick the Nth percentile
 * from the bottom. A small percentile (default 5%) is robust to the rare
 * splat that strays below the actual floor, while still hugging the real
 * surface.
 *
 * Returns the world-space Y of the estimated floor, or null if not enough
 * splats fall in the radius.
 */
export function estimateSplatFloorY(splatMesh, {
  targetX = 0,
  targetZ = 0,
  radius = 1.5,
  percentile = 0.05,
  minSamples = 20,
} = {}) {
  if (!splatMesh || typeof splatMesh.forEachSplat !== 'function') return null

  splatMesh.updateMatrixWorld(true)
  const m = splatMesh.matrixWorld
  const r2 = radius * radius
  const ys = []
  const worldPos = new THREE.Vector3()

  splatMesh.forEachSplat((i, center /*, scales, quat, opacity, color*/) => {
    worldPos.copy(center).applyMatrix4(m)
    const dx = worldPos.x - targetX
    const dz = worldPos.z - targetZ
    if (dx * dx + dz * dz <= r2) ys.push(worldPos.y)
  })

  if (ys.length < minSamples) return null
  ys.sort((a, b) => a - b)
  const idx = Math.max(0, Math.min(ys.length - 1, Math.floor(ys.length * percentile)))
  return ys[idx]
}
